/**
 * Simple encryption/decryption utility for job IDs in URLs
 * Uses a basic XOR cipher with a secret key for URL-safe encryption
 */

// Simple secret key - in production, this should be in environment variables
const SECRET_KEY = 'VA_SPROUTS_JOB_KEY_2024';

/**
 * Convert string to base64url (URL-safe base64)
 * @param {string} str - String to encode
 * @returns {string} - Base64url encoded string
 */
const toBase64Url = (str) => {
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

/**
 * Convert base64url back to string
 * @param {string} base64url - Base64url encoded string
 * @returns {string} - Decoded string
 */
const fromBase64Url = (base64url) => {
  // Add padding if needed
  let padded = base64url.replace(/-/g, '+').replace(/_/g, '/');
  while (padded.length % 4) {
    padded += '=';
  }
  return atob(padded);
};

/**
 * Simple XOR encryption
 * @param {string} text - Text to encrypt
 * @param {string} key - Encryption key
 * @returns {string} - Encrypted text
 */
const xorEncrypt = (text, key) => {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(
      text.charCodeAt(i) ^ key.charCodeAt(i % key.length)
    );
  }
  return result;
};

/**
 * Simple XOR decryption (same as encryption for XOR)
 * @param {string} encryptedText - Encrypted text
 * @param {string} key - Decryption key
 * @returns {string} - Decrypted text
 */
const xorDecrypt = (encryptedText, key) => {
  return xorEncrypt(encryptedText, key); // XOR is symmetric
};

/**
 * Encrypt a job ID for use in URLs (8 characters)
 * Creates a deterministic short ID that can be computed from UUID
 * @param {string} jobId - Job UUID to encrypt
 * @returns {string} - Short encoded, URL-safe string (8 chars)
 */
export const encryptJobId = (jobId) => {
  if (!jobId) return '';
  
  try {
    // Create a deterministic hash from the UUID
    // This can be recomputed from the UUID for database lookup
    let hash = 0;
    const cleanId = jobId.replace(/-/g, '').toLowerCase();
    
    for (let i = 0; i < cleanId.length; i++) {
      const char = cleanId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Convert to positive number and base36 for URL-safe 8-char string
    const positiveHash = Math.abs(hash);
    let shortId = positiveHash.toString(36).substring(0, 8);
    
    // Pad with zeros if needed to ensure 8 characters
    shortId = shortId.padStart(8, '0');
    
    return shortId.toLowerCase();
  } catch (error) {
    console.error('Encryption error:', error);
    return jobId.substring(0, 8).replace(/-/g, '').toLowerCase();
  }
};

/**
 * Decrypt a job ID from URL (8-character encoded ID)
 * Returns the short ID for database lookup
 * @param {string} encryptedId - 8-character encoded ID from URL
 * @returns {string|null} - Short ID for lookup or null if invalid
 */
export const decryptJobId = (encryptedId) => {
  if (!encryptedId) return null;
  
  // For 8-character encoded ID, return it for database lookup
  // The job service should compute the same short ID from stored UUIDs
  if (encryptedId.length === 8 && /^[0-9a-z]+$/i.test(encryptedId)) {
    return encryptedId.toLowerCase();
  }
  
  // Fallback: try old decryption method for backward compatibility
  try {
    const encrypted = fromBase64Url(encryptedId);
    const decrypted = xorDecrypt(encrypted, SECRET_KEY);
    const parts = decrypted.split('|');
    const jobId = parts[0];
    
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(jobId)) {
      return jobId;
    }
  } catch (error) {
    // Ignore errors for old format
  }
  
  return null;
};

/**
 * Compute short ID from UUID (for database lookup)
 * This function can be used by the job service to find jobs by short ID
 * @param {string} jobId - Job UUID
 * @returns {string} - 8-character short ID
 */
export const computeShortIdFromUUID = (jobId) => {
  if (!jobId) return null;
  
  // Create a consistent 8-character ID from the UUID
  // Use the first 8 characters of the UUID (without hyphens)
  const cleanUuid = jobId.replace(/-/g, '');
  return cleanUuid.substring(0, 8).toLowerCase();
};

/**
 * Check if a string is an encrypted job ID (8-character hex)
 * @param {string} str - String to check
 * @returns {boolean} - True if it looks like an encrypted ID
 */
export const isEncryptedJobId = (str) => {
  if (!str || typeof str !== 'string') return false;
  
  // Check for new 8-character hex format
  if (str.length === 8 && /^[0-9a-f]+$/i.test(str)) {
    return true;
  }
  
  // Check for old base64url format (backward compatibility)
  const base64UrlRegex = /^[A-Za-z0-9_-]+$/;
  return base64UrlRegex.test(str) && str.length > 10 && str.length < 100;
};
